import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import { useApplicationContext } from "../../../context/ApplicationContext";
import SuiteList from "../components/SuiteList";
import NewTestSuite from "../components/suites/NewTestSuite";
import { listRegressionPlans_API } from "../../../api/controller/RegressionController";
import RegressionHeader from "../components/RegressionHeader";
import WebhookOutlinedIcon from "@mui/icons-material/WebhookOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import {
  createRegressionPlan_API,
  updateRegressionPlan_API,
} from "../../../api/controller/RegressionController";
import RegressionPlanDrawer from "../Drawer/RegressionPlanDrawer ";
import { Add } from "@mui/icons-material";

export default function RegressionContainer() {
  const { activeProject } = useApplicationContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create"); // "create" | "edit"
  const [activeSuite, setActiveSuite] = useState(null);
  const [showNewSuite, setShowNewSuite] = useState(false);
  const [editingSuite, setEditingSuite] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [suites, setSuites] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRegressionPlans = async () => {
    if (!activeProject?.name) {
      setLoading(false);
      return;
    }
    console.log(" Calling list API");

    try {
      const response = await listRegressionPlans_API(activeProject.name);
      console.log("API Response:", response);

      if (Array.isArray(response?.data) && response.data.length > 0) {
        const mappedSuites = response.data.map((plan) => ({
          id: plan.name,
          name: plan.name,
          description: plan.description,
          releaseName: plan.releaseName,
          totalTests: plan.testCaseCount || 0,
          testCases: plan.testCases || [],
          createdBy: plan.createdBy,
          lastRun: plan.lastModifiedAt,
        }));

        setSuites(mappedSuites);
      } else {
        setSuites([]); // <- explicitly clear suites
      }
    } catch (error) {
      console.error("Error fetching regression plans:", error);
      // TEMP fallback
      setSuites((prev) => prev);
    } finally {
      setLoading(false);
    }
  };

  const filteredSuites = suites.filter(
    (suite) =>
      suite.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (suite.description &&
        suite.description.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  useEffect(() => {
    fetchRegressionPlans();
  }, [activeProject]);

  const handleNewSuiteClick = () => {
    setDrawerMode("create");
    setActiveSuite(null);
    setDrawerOpen(true);
  };
  const handleEditSuiteClick = (suite) => {
    setDrawerMode("edit");
    setActiveSuite(suite);
    setDrawerOpen(true);
  };

  const handleCancelNewSuite = () => {
    setEditingSuite(null);
    setShowNewSuite(false);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <CircularProgress
          sx={{ color: (theme) => theme.palette.primary.contrastText }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        bgcolor: (theme) => theme.palette.background.default,
        px: 3, // page-level padding
        py: 2,
        boxSizing: "border-box",
      }}
    >
      {showNewSuite ? (
        <NewTestSuite
          onCancel={() => {
            setShowNewSuite(false);
            setEditingSuite(null);
          }}
          initialSuite={editingSuite} // pass suite to edit
          onPlanCreated={fetchRegressionPlans} // refresh list after create/edit
        />
      ) : (
        <>
          {/* Only render header + suite list when showNewSuite is false */}
          {!showNewSuite && (
            <RegressionHeader
              onNewSuiteClick={handleNewSuiteClick}
              hasSuites={suites.length > 0}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />
          )}

          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="400px"
            >
              <CircularProgress />
            </Box>
          ) : suites.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight={400}
              gap={2}
              sx={{ mt: 4 }}
            >
              <WebhookOutlinedIcon
                sx={{
                  fontSize: 80,
                  color: (theme) => theme.palette.text.secondary,
                  opacity: 0.7,
                }}
              />
              <Typography variant="h5" color="text.secondary" fontWeight={500}>
                No regression plans found
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ opacity: 0.8 }}
              >
                Create your first regression plan to get started
              </Typography>
              <Button
                startIcon={<Add />}
                onClick={handleNewSuiteClick}
                sx={{
                  mt: 2,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  height: 40,
                  px: 3,
                  background:
                    "linear-gradient(90deg, hsl(var(--cyan)), hsl(var(--emerald)))",
                  color: "hsl(var(--primary-foreground))",
                  boxShadow: "0 4px 14px hsl(var(--cyan) / 0.35)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, hsl(var(--cyan) / 0.9), hsl(var(--emerald) / 0.9))",
                    boxShadow: "0 6px 20px hsl(var(--cyan) / 0.45)",
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                }}
              >
                Create New Plan
              </Button>
            </Box>
          ) : filteredSuites.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight={400}
              gap={2}
              sx={{ mt: 4 }}
            >
              <SearchOffOutlinedIcon
                sx={{ fontSize: 80, color: "grey.400", opacity: 0.7 }}
              />
              <Typography variant="h5" color="text.secondary" fontWeight={500}>
                No results found
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ opacity: 0.8 }}
              >
                No regression plans match your search "{searchTerm}"
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setSearchTerm("")}
                sx={{ mt: 2 }}
              >
                Clear Search
              </Button>
            </Box>
          ) : (
            <SuiteList
              // Use the filtered array here instead of the original 'suites'
              suites={filteredSuites}
              onEdit={(suite) => {
                setActiveSuite(suite);
                setDrawerMode("edit");
                setDrawerOpen(true);
              }}
              onOpenSuite={(suite, existingTestCases = []) => {
                // merge suite info with its test cases
                setEditingSuite({ ...suite, testCases: existingTestCases });
                setShowNewSuite(true);
              }}
              onSuiteDeleted={(deletedSuiteName) => {
                setSuites((prev) =>
                  prev.filter((s) => s.name !== deletedSuiteName),
                );
              }}
              onPlanCreated={(updatedSuite) => {
                setSuites((prev) =>
                  prev.map((s) =>
                    s.name === updatedSuite.name ? updatedSuite : s,
                  ),
                );
              }}
            />
          )}
        </>
      )}
      <RegressionPlanDrawer
        open={drawerOpen}
        mode={drawerMode}
        initialData={activeSuite}
        onClose={() => setDrawerOpen(false)}
        fetchRegressionPlans={fetchRegressionPlans}
        onSubmit={async (payload) => {
          try {
            if (drawerMode === "create") {
              await createRegressionPlan_API(payload);
            } else {
              await updateRegressionPlan_API(payload); // edit API
            }
            await fetchRegressionPlans(); // always refresh list
          } catch (err) {
            console.error(err);
          } finally {
            setDrawerOpen(false);
          }
        }}
      />
    </Box>
  );
}
